import React, { useState } from 'react';
import { StyleSheet, View, Text, TouchableOpacity, TextInput, ActivityIndicator } from 'react-native';
import { Colors } from '@/constants/theme';
import FontAwesome5 from '@expo/vector-icons/FontAwesome5';
import { SafeAreaView } from 'react-native-safe-area-context';
import { StatusBar } from 'expo-status-bar';
import { useRouter } from 'expo-router';
import { verifyCode, fetchStreamToken, registerDeviceToken, fetchUserProfile } from '@/services/universe/client';
import { savePassportToken, saveUserProfile } from '@/services/auth/tokenStore';
import { registerForPushNotificationsAsync } from '@/utils/registerForPushNotificationsAsync';
import { useIsTablet } from '@/hooks/useIsTablet';

export default function TokenVerification() {
    const router = useRouter();
    const isTablet = useIsTablet();
    const [token, setToken] = useState('');
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const handleLogin = async () => {
        if (token.length < 6) {
            setErrorMessage("Please enter a valid 6-digit code");
            return;
        }

        setLoading(true);
        setErrorMessage('');
        try {
            console.log("Verifying code:", token);
            const data = await verifyCode(token);
            const passportToken = data.access_token;

            if (passportToken) {
                // Save tokens to SecureStore
                await savePassportToken(passportToken);
                
                // Verify token and get stream credentials
                const { streamUserId, streamToken } = await fetchStreamToken();
                console.log('Stream credentials fetched:', streamUserId ? "YES" : "NO");

                if (streamUserId && streamToken) {
                    // Fetch and save user profile data
                    try {
                        const profileData = await fetchUserProfile();
                        await saveUserProfile(profileData.name, profileData.image);
                        console.log('User profile fetched and saved');
                    } catch (profileError) {
                        console.warn('Failed to fetch/save user profile:', profileError);
                    }

                    // Register device for push notifications
                    try {
                        const pushToken = await registerForPushNotificationsAsync();
                        if (pushToken) {
                            await registerDeviceToken(pushToken.expoPushToken);
                            console.log('Device registered for push notifications successfully');
                        }
                    } catch (pushError) {
                        console.warn('Failed to register for push notifications:', pushError);
                        // We don't block login if push registration fails
                    }

                    // Navigate to main app
                    router.replace('/(tabs)');
                } else {
                    setErrorMessage("Failed to obtain stream credentials");
                }
            } else {
                setErrorMessage("The code you entered is incorrect");
            }
        } catch (error: any) {
            console.warn("Unexpected verification error:", error);
            setErrorMessage("An unexpected error occurred. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <View style={[styles.container, isTablet && styles.containerTablet]}>
            <StatusBar style="dark" />
            
            <View style={isTablet ? styles.mainWrapperTablet : styles.fullContent}>
                {/* Header */}
                <View style={styles.header}>
                    <TouchableOpacity onPress={() => router.back()} style={styles.backButton}>
                        <FontAwesome5 name="chevron-left" size={20} color="#1a1a1a" />
                    </TouchableOpacity>
                    <Text style={styles.headerTitle}>Token Verification</Text>
                </View>

                <SafeAreaView style={styles.content} edges={isTablet ? ['bottom'] : ['top', 'bottom']}>
                    {/* Key Icon */}
                    <View style={styles.iconContainer}>
                        <FontAwesome5 name="key" size={60} color={Colors.brand} />
                    </View>

                    {/* Description */}
                    <Text style={styles.description}>
                        Enter the 6-digit verification code from Universe to continue logging in
                    </Text>

                    {/* Token Input */}
                    <TextInput
                        style={[
                            styles.input,
                            { fontSize: token ? 24 : 16 },
                            errorMessage ? styles.inputError : null
                        ]}
                        placeholder="Enter the 6-digit code"
                        placeholderTextColor="#ccc"
                        value={token}
                        onChangeText={(text) => {
                            setToken(text);
                            if (errorMessage) setErrorMessage('');
                        }}
                        keyboardType="number-pad"
                        maxLength={6}
                        editable={!loading}
                    />

                    {/* Error Message */}
                    {errorMessage ? (
                        <Text style={styles.errorText}>{errorMessage}</Text>
                    ) : null}

                    {/* Login Button */}
                    <TouchableOpacity 
                        style={[styles.loginButton, loading && styles.loginButtonDisabled]}
                        activeOpacity={0.8}
                        onPress={handleLogin}
                        disabled={loading}
                    >
                        {loading ? (
                            <ActivityIndicator color="white" />
                        ) : (
                            <Text style={styles.loginButtonText}>Login</Text>
                        )}
                    </TouchableOpacity>
                </SafeAreaView>
            </View>
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: 'white',
    },
    containerTablet: {
        backgroundColor: '#f8fbff',
        justifyContent: 'center',
        alignItems: 'center',
    },
    mainWrapperTablet: {
        width: '100%',
        maxWidth: 400,
        maxHeight: 800,
        height: '90%',
        borderRadius: 40,
        overflow: 'hidden',
        elevation: 10,
        shadowColor: "#000",
        shadowOffset: {
            width: 0,
            height: 10,
        },
        shadowOpacity: 0.1,
        shadowRadius: 20,
        backgroundColor: 'white',
    },
    fullContent: {
        flex: 1,
    },
    header: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 20,
        paddingVertical: 15,
        borderBottomWidth: 1,
        borderBottomColor: '#f0f0f0',
    },
    backButton: {
        padding: 5,
    },
    headerTitle: {
        fontSize: 20,
        fontWeight: 'bold',
        marginLeft: 20,
        color: '#1a1a1a',
    },
    content: {
        flex: 1,
        alignItems: 'center',
        paddingHorizontal: 30,
        paddingTop: 60,
    },
    iconContainer: {
        marginBottom: 30,
    },
    description: {
        fontSize: 16,
        color: '#444',
        textAlign: 'center',
        lineHeight: 24,
        marginBottom: 40,
    },
    input: {
        width: '100%',
        height: 50,
        borderWidth: 1,
        borderColor: '#ddd',
        borderRadius: 8,
        paddingHorizontal: 15,
        textAlign: 'center',
        color: '#1a1a1a',
        marginBottom: 20,
    },
    inputError: {
        borderColor: '#FF5A5F',
        borderWidth: 1.5,
    },
    errorText: {
        color: '#FF5A5F',
        fontSize: 14,
        alignSelf: 'flex-start',
        marginTop: -15,
        marginBottom: 20,
    },
    loginButton: {
        backgroundColor: Colors.brand,
        width: '100%',
        paddingVertical: 14,
        borderRadius: 8,
        alignItems: 'center',
        justifyContent: 'center',
    },
    loginButtonDisabled: {
        opacity: 0.7,
    },
    loginButtonText: {
        color: 'white',
        fontSize: 18,
        fontWeight: '600',
    },
});
